import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { getLoginRequest } from "../config/authConfig";
import { redirectToLoginWithError } from "../utils/authRedirect";
import { SESSIE_VERLOPEN } from "../utils/authMessages";

// Base64URL decode helper
function decodeJwtPart(part) {
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getJwtExp(token) {
  try {
    const [, payload] = token.split(".");
    const claims = decodeJwtPart(payload) || {};
    const exp = Number(claims.exp);
    return Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

/**
 * useToken returns a tokenProvider function that:
 * - Ensures an active account (sets if only one in cache)
 * - Tries acquireTokenSilent
 * - If token is about to expire (<120s), forces refresh
 * - On interaction required, falls back to popup (when allowed) or redirect
 */
// Tijdstip (ms) waarop de laatste redirect gestart is; guard vervalt na 15s
// zodat een blijvend geblokkeerde flow niet permanent vastloopt.
let _redirectStartedAt = 0;
const REDIRECT_GUARD_MS = 15000;

export function useToken({
  allowPopup = true,
  loginErrorMessage = SESSIE_VERLOPEN,
} = {}) {
  const { instance, accounts, inProgress } = useMsal();

  const tokenProvider = useCallback(async () => {
    // Ensure active account
    let account = instance.getActiveAccount();
    if (!account && Array.isArray(accounts) && accounts.length === 1) {
      instance.setActiveAccount(accounts[0]);
      account = accounts[0];
    }

    // If no account, and no interaction ongoing, trigger login
    if (!account) {
      if (inProgress && inProgress !== InteractionStatus.None) return "";
      if (Date.now() - _redirectStartedAt >= REDIRECT_GUARD_MS) {
        _redirectStartedAt = Date.now();
        if (loginErrorMessage) redirectToLoginWithError(loginErrorMessage);
        await instance.loginRedirect(getLoginRequest());
      }
      return ""; // suppress error boundary overlay
    }

    // Try silent first
    try {
      let res = await instance.acquireTokenSilent({ ...getLoginRequest(), account });
      _redirectStartedAt = 0; // geslaagd: reset guard
      // Proactive refresh when token is expiring in <120s
      const exp = getJwtExp(res.accessToken);
      const now = Math.floor(Date.now() / 1000);
      if (exp && exp - now < 120) {
        try {
          res = await instance.acquireTokenSilent({
            ...getLoginRequest(),
            account,
            forceRefresh: true,
          });
        } catch {
          // ignore and use previous token; next call will fall back if needed
        }
      }
      return res.accessToken;
    } catch (err) {
  const code = err?.errorCode || err?.subError || err?.message || "";
      const isInteractionRequired =
        err instanceof InteractionRequiredAuthError ||
        /interaction_required|login_required|consent_required/i.test(code) ||
        /AADSTS70008|AADSTS50058|AADSTS500133/i.test(code);

      if (isInteractionRequired) {
        // Avoid starting a second interaction while one is in progress
        if (inProgress && inProgress !== InteractionStatus.None) return "";
        // Prefer popup in non-standalone contexts if allowed
        if (
          allowPopup &&
          typeof window !== "undefined" &&
          !window.matchMedia("(display-mode: standalone)").matches
        ) {
          try {
            const pop = await instance.acquireTokenPopup({ ...getLoginRequest(), account });
            return pop.accessToken;
          } catch {
            // fall through to redirect
          }
        }
        // Suppress intrusive error banner for expired refresh token (AADSTS70008)
        const showError = loginErrorMessage && !/AADSTS70008/i.test(code);
        if (Date.now() - _redirectStartedAt >= REDIRECT_GUARD_MS) {
          _redirectStartedAt = Date.now();
          if (showError) redirectToLoginWithError(loginErrorMessage);
          await instance.loginRedirect(getLoginRequest());
        }
        return ""; // always suppress throwing to avoid dev error overlay
      }

      // Unknown error: bubble up
      throw err;
    }
  }, [accounts, instance, inProgress, allowPopup, loginErrorMessage]);

  return tokenProvider;
}
